import { FolderLine } from "@dropbox/dig-icons/assets";
import { useIsMobileSize } from "@mirage/shared/responsive/mobile";
import {
  getLocalFileTimestamp,
  getPathForDisplay,
  getPersonForMetadata,
  getTimeAgoStringFromTimestamp,
} from "../util/resultUtil";
import { MetadataItem } from "./MetadataItem";
import { PersonMetadata } from "./PersonMetadata";

import type { SearchResult } from "@mirage/service-dbx-api";
import type { PersonObject } from "@mirage/service-people/types";
import type { Recommendation } from "@mirage/shared/search/recommendation";
import { isDesktopLocalFile } from "@mirage/shared/search/search-result";
import type { RefObject } from "react";

type FilePathMetadataProps = {
  result: SearchResult | Recommendation;
  pathSegments: string[];
  isTooltipContent?: boolean;
  triggerRef?: RefObject<HTMLDivElement>;
  onClickPerson?: (person: PersonObject) => void;
};

export const FilePathMetadata = ({
  result,
  pathSegments,
  isTooltipContent,
  triggerRef,
  onClickPerson,
}: FilePathMetadataProps) => {
  const { sender, creator, lastModifier, filteredModifier } = result;
  const isMobileSize = useIsMobileSize();

  const timestamp = isDesktopLocalFile(result)
    ? getLocalFileTimestamp(result)
    : getTimeAgoStringFromTimestamp(
        filteredModifier?.modifiedAtMs ?? result.providerUpdateAtMs,
        true,
      );
  const metadataTitle = getPathForDisplay(pathSegments, isTooltipContent);

  const renderPersonMetadata = () => {
    const { person, i18nKey } = getPersonForMetadata({
      sender,
      creator,
      lastModifier,
      filteredModifier,
    });

    if (!i18nKey || !person) {
      return null;
    }

    return (
      <MetadataItem
        title={
          <PersonMetadata
            i18nKey={i18nKey}
            person={person}
            onClickPerson={onClickPerson}
          />
        }
      />
    );
  };

  return (
    <>
      {pathSegments.length > 0 && (
        <MetadataItem
          title={metadataTitle}
          iconSrc={FolderLine}
          triggerRef={triggerRef}
          className="meticulous-redact-recording"
        />
      )}
      {!isTooltipContent && !isMobileSize && renderPersonMetadata()}
      {!isTooltipContent && timestamp && (
        <MetadataItem title={timestamp} className="unlocalizableString" />
      )}
    </>
  );
};
